// Shared, reusable widget renderers for the Project Reporting Hub.
//
// Every report widget lives here ONCE, keyed by widgetKey, and is reused by all
// report views (Hardware / Software / Certification) via renderWidget(). This
// is the single place that maps a widgetKey -> React renderer, matching the
// widgetKeys defined in components/AccountManagement/reportWidgetCatalog.js.
//
// renderWidget(widgetKey, ctx) -> JSX
//   ctx = {
//     product : 'Hardware' | 'Software' | 'Certification',
//     data    : the dashboard/report data object for the current view,
//     images  : { trend, throughput, logo }   (Certification only),
//     imgBusy : { charts, logo },              (Certification only),
//     onGenerateCharts, onGenerateLogo,        (Certification only),
//   }
import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { SeverityPie, DomainBars } from './charts';

const rowBg = (s) => (s === 'red' ? 'bg-red-500 text-white' : s === 'green' ? 'bg-green-500 text-white' : 'bg-white text-black');

export const statusColor = (v) => {
  const s = String(v || '').toLowerCase();
  if (s.includes('certif')) return 'text-green-600';
  if (s.includes('lab')) return 'text-blue-600';
  if (s.includes('pretest') || s.includes('pre-test')) return 'text-orange-600';
  if (s.includes('plan')) return 'text-gray-500';
  return 'text-gray-800';
};

// Generic table used by CS/CG tracking widgets and the certification fallback.
export function Table({ columns, rows, keys, empty }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-800  py-8 text-center">
        <div className="text-lg font-semibold text-gray-300">0 items</div>
        <div className="text-xs text-gray-500">{empty}</div>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-gray-300">
      <table className="w-full text-xs bg-white text-black">
        <thead><tr className="bg-gray-200">{columns.map((c) => <th key={c} className="py-2 px-2 text-left font-semibold border-b border-gray-300 whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-200">
              {keys.map((k) => <td key={k} className={`py-1.5 px-2 ${k === 'status' ? statusColor(r[k]) + ' font-semibold' : ''}`}>{r[k] != null ? String(r[k]) : ''}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────── Hardware widgets ───────────────────────── */
const HW_STATE_CELL = {
  New: 'bg-red-600 text-white',
  Analysis: 'bg-yellow-400 text-black',
  Info: 'bg-white text-black',
  WfR: 'bg-amber-200 text-black',
  CfM: 'bg-green-500 text-white',
};

function HwHighlight({ data }) {
  const rows = data?.highlightIssues || [];
  const cols = ['Issue Code', 'Subject', 'Assignee', 'State', 'Severity', 'Issue Domain', 'Model Name', 'Deadline', 'Phase', 'Existence'];
  return (
    <div className="overflow-x-auto border border-gray-300">
      <table className="w-full text-xs bg-white">
        <thead>
          <tr className="bg-gray-200 text-gray-800">
            {cols.map((h) => <th key={h} className="py-2 px-2 text-left font-semibold border-b border-gray-300 whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} className="py-3 text-center text-gray-500 bg-white">No Blocker/Critical issues</td></tr>}
          {rows.map((r) => (
            <tr key={r.issueCode} className={`${rowBg(r.rowStyle)} border-b border-gray-200`}>
              <td className="py-1.5 px-2 font-mono text-blue-700 whitespace-nowrap">{r.issueCode}</td>
              <td className="py-1.5 px-2 max-w-xs truncate" title={r.subject}>{r.subject}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.assignee || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.state}</td>
              <td className="py-1.5 px-2">{r.severity}</td>
              <td className="py-1.5 px-2">{r.issueDomain || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.modelName || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.deadline || '-'}</td>
              <td className="py-1.5 px-2">{r.phase || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.existence != null ? `${r.existence}Day(s)` : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StateSummary({ data, stateCell }) {
  const rows = data?.stateSummary || [];
  const t = rows.reduce(
    (a, r) => ({ total: a.total + r.total, blocker: a.blocker + r.blocker, critical: a.critical + r.critical, major: a.major + r.major }),
    { total: 0, blocker: 0, critical: 0, major: 0 }
  );
  return (
    <div className="inline-block border border-gray-300 overflow-hidden">
      <table className="text-sm bg-white text-black">
        <thead>
          <tr className="bg-slate-500 text-white">
            {['State', 'Total', 'Blocker', 'Critical', 'Major'].map((h) => <th key={h} className="py-2 px-4 font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.state} className="border-b border-gray-200 text-center">
              <td className={`py-2 px-4 font-bold ${stateCell[r.state] || 'bg-white'}`}>{r.state}</td>
              <td className="py-2 px-4 bg-slate-100">{r.total}</td>
              <td className="py-2 px-4">{r.blocker}</td>
              <td className="py-2 px-4">{r.critical}</td>
              <td className="py-2 px-4">{r.major}</td>
            </tr>
          ))}
          <tr className="bg-slate-500 text-white font-bold text-center">
            <td className="py-2 px-4">Total</td>
            <td className="py-2 px-4">{t.total}</td>
            <td className="py-2 px-4">{t.blocker}</td>
            <td className="py-2 px-4">{t.critical}</td>
            <td className="py-2 px-4">{t.major}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────── Software widgets ───────────────────────── */
const SW_STATE_CELL = {
  New: 'bg-red-600 text-white',
  Analysis: 'bg-yellow-400 text-black',
  Info: 'bg-blue-500 text-white',
  CfM: 'bg-green-500 text-white',
};

function SwHighlight({ data }) {
  const rows = data?.highlightIssues || [];
  const cols = ['Issue Code', 'Assignee', 'State', 'Subject', 'Module', 'Deadline', 'Severity', 'Existence'];
  return (
    <div className="overflow-x-auto border border-gray-300">
      <table className="w-full text-xs bg-white">
        <thead><tr className="bg-gray-200 text-gray-800">
          {cols.map((h) => <th key={h} className="py-2 px-2 text-left font-semibold border-b border-gray-300 whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} className="py-3 text-center text-gray-500 bg-white">No Blocker/Critical issues</td></tr>}
          {rows.map((r) => (
            <tr key={r.issueCode} className={`${rowBg(r.rowStyle)} border-b border-gray-200`}>
              <td className="py-1.5 px-2 font-mono text-blue-700 whitespace-nowrap">{r.issueCode}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.assignee || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.state}</td>
              <td className="py-1.5 px-2 max-w-xs truncate" title={r.subject}>{r.subject}</td>
              <td className="py-1.5 px-2">{r.module || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.deadline || '-'}</td>
              <td className="py-1.5 px-2">{r.severity}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.existence != null ? `${r.existence}Day(s)` : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SwStability({ data }) {
  const st = data?.stabilityTable || { rows: [], total: 0 };
  return (
    <div className="inline-block border border-gray-300 overflow-hidden">
      <table className="text-sm bg-white text-black">
        <thead><tr className="bg-slate-500 text-white"><th className="py-2 px-4">Stability Issue</th><th className="py-2 px-4">Total</th></tr></thead>
        <tbody>
          {st.rows.map((r) => (
            <tr key={r.tag} className="border-b border-gray-200 text-center">
              <td className="py-2 px-4 font-semibold">{r.tag}</td>
              <td className="py-2 px-4">{r.total}</td>
            </tr>
          ))}
          <tr className="bg-slate-500 text-white font-bold text-center"><td className="py-2 px-4">Total</td><td className="py-2 px-4">{st.total}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

/* ──────────────────────── Certification widgets ──────────────────────── */
const CERT_STATE_CELL = {
  New: 'bg-red-600 text-white', Analysis: 'bg-yellow-400 text-black', Info: 'bg-blue-500 text-white',
  'Vendor WfR': 'bg-amber-500 text-black', WFR: 'bg-gray-300 text-black', CfM: 'bg-green-500 text-white',
  Certified: 'bg-green-600 text-white', Lab: 'bg-blue-600 text-white', Pretest: 'bg-amber-500 text-black',
};

function CertHighlight({ data }) {
  const rows = data?.highlight || [];
  const cols = ['Issue Code', 'Subject', 'Owner', 'Site', 'State', 'Severity', 'Cert', 'Deadline', 'Existence'];
  return (
    <div className="overflow-x-auto border border-gray-300">
      <table className="w-full text-xs bg-white text-black">
        <thead><tr className="bg-gray-200">{cols.map((c) => <th key={c} className="py-2 px-2 text-left font-semibold border-b border-gray-300 whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} className="py-3 text-center text-gray-500">No highlight issues</td></tr>}
          {rows.map((r) => (
            <tr key={r.issueCode} className={`border-b border-gray-200 ${r.rowStyle === 'red' ? 'bg-red-500 text-white' : r.rowStyle === 'green' ? 'bg-green-500 text-white' : ''}`}>
              <td className="py-1.5 px-2 font-mono text-blue-700 whitespace-nowrap">{r.issueCode}</td>
              <td className="py-1.5 px-2 max-w-xs truncate" title={r.subject}>{r.subject}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.owner || '-'}</td>
              <td className="py-1.5 px-2">{r.site}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.state}</td>
              <td className="py-1.5 px-2">{r.severity || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.cert || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.deadline || '-'}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{r.existence || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CertStateTable({ data }) {
  const cs = data?.stateByScope || { scopes: [], rows: [] };
  return (
    <div className="overflow-x-auto border border-gray-300">
      <table className="w-full text-xs bg-white text-black">
        <thead><tr className="bg-slate-500 text-white"><th className="py-2 px-2">State</th><th className="py-2 px-2">Total</th>{cs.scopes.map((s) => <th key={s} className="py-2 px-2">{s}</th>)}</tr></thead>
        <tbody>
          {cs.rows.map((r) => (
            <tr key={r.state} className="border-b border-gray-200 text-center">
              <td className="py-1.5 px-2 font-semibold text-left">{r.state}</td>
              <td className="py-1.5 px-2">{r.total}</td>
              {r.cells.map((c, i) => <td key={i} className="py-1.5 px-2">{c || ''}</td>)}
            </tr>
          ))}
          {cs.rows.length === 0 && <tr><td colSpan={2} className="py-3 text-center text-gray-500">No data</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function CertStateSeverity({ data }) {
  const rows = data?.stateBySeverity?.rows || [];
  const t = rows.reduce((a, r) => ({ total: a.total + r.total, blocker: a.blocker + r.blocker, critical: a.critical + r.critical, major: a.major + r.major }), { total: 0, blocker: 0, critical: 0, major: 0 });
  return (
    <div className="overflow-x-auto border border-gray-300">
      <table className="w-full text-xs bg-white text-black">
        <thead><tr className="bg-slate-500 text-white">{['State', 'Total', 'Blocker', 'Critical', 'Major'].map((c) => <th key={c} className="py-2 px-2">{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.state} className="border-b border-gray-200 text-center">
              <td className={`py-1.5 px-2 font-semibold ${CERT_STATE_CELL[r.state] || ''}`}>{r.state}</td>
              <td className="py-1.5 px-2 bg-slate-100">{r.total}</td>
              <td className="py-1.5 px-2">{r.blocker}</td>
              <td className="py-1.5 px-2">{r.critical}</td>
              <td className="py-1.5 px-2">{r.major}</td>
            </tr>
          ))}
          <tr className="bg-slate-500 text-white font-bold text-center">
            <td className="py-1.5 px-2">Total</td><td className="py-1.5 px-2">{t.total}</td><td className="py-1.5 px-2">{t.blocker}</td><td className="py-1.5 px-2">{t.critical}</td><td className="py-1.5 px-2">{t.major}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CertOwnerMatrix({ data }) {
  const om = data?.ownerMatrix || { states: [], rows: [] };
  return (
    <div className="overflow-x-auto border border-gray-300">
      <table className="w-full text-xs bg-white text-black">
        <thead><tr className="bg-slate-500 text-white"><th className="py-2 px-2">Owner</th>{om.states.map((s) => <th key={s} className="py-2 px-2">{s}</th>)}<th className="py-2 px-2">Total</th></tr></thead>
        <tbody>
          {om.rows.map((r) => (
            <tr key={r.owner} className="border-b border-gray-200 text-center">
              <td className="py-1.5 px-2 font-semibold text-left">{r.owner}</td>
              {r.counts.map((c, i) => <td key={i} className="py-1.5 px-2">{c || ''}</td>)}
              <td className="py-1.5 px-2 font-semibold">{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PAGE_SIZE = 10;
function CertWeeklyHighlight({ data }) {
  const rows = data?.rows || [];
  const [page, setPage] = React.useState(1);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const slice = rows.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);

  if (rows.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-800 py-10 text-center">
        <div className="text-lg font-semibold text-gray-200">No highlight issues at this time</div>
        <div className="text-sm text-gray-400">All items are on track based on the latest data.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto border border-gray-300">
        <table className="w-full text-xs bg-white text-black">
          <thead><tr className="bg-gray-200">{['Certification', 'Target CS', 'Status', 'Highlight', 'Remark'].map((c) => <th key={c} className="py-2 px-2 text-left font-semibold border-b border-gray-300">{c}</th>)}</tr></thead>
          <tbody>
            {slice.map((r, i) => (
              <tr key={i} className="border-b border-gray-200">
                <td className="py-1.5 px-2 font-semibold">{r.certification}</td>
                <td className="py-1.5 px-2 text-green-700 font-semibold">{r.targetCs}</td>
                <td className={`py-1.5 px-2 font-semibold ${statusColor(r.status)}`}>{r.status}</td>
                <td className="py-1.5 px-2 text-blue-800">{r.highlight}</td>
                <td className="py-1.5 px-2">{r.remark}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-3 text-sm">
          <button onClick={() => setPage(1)} disabled={cur === 1} className="px-2 py-1 bg-gray-800 disabled:opacity-40">«</button>
          <button onClick={() => setPage(cur - 1)} disabled={cur === 1} className="px-2 py-1  bg-gray-800 disabled:opacity-40">‹</button>
          {Array.from({ length: pages }, (_, i) => i + 1).slice(Math.max(0, cur - 3), cur + 2).map((p) => (
            <button key={p} onClick={() => setPage(p)} className={`px-3 py-1 ${p === cur ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'}`}>{p}</button>
          ))}
          <button onClick={() => setPage(cur + 1)} disabled={cur === pages} className="px-2 py-1 bg-gray-800 disabled:opacity-40">›</button>
          <button onClick={() => setPage(pages)} disabled={cur === pages} className="px-2 py-1 bg-gray-800 disabled:opacity-40">»</button>
        </div>
      )}
    </div>
  );
}

const ImageWidget = ({ src, label, onGenerate, generating }) => (
  src ? (
    <div className="overflow-x-auto bg-white border border-gray-300 p-2 relative group">
      <img src={src} alt={label} className="max-w-full h-auto max-h-[420px] object-contain mx-auto" />
      {onGenerate && (
        <button
          onClick={onGenerate}
          disabled={generating}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-xs text-white"
          title={`Regenerate ${label}`}
        >
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      )}
    </div>
  ) : (
    <div className="bg-gray-900 border border border-gray-700 py-10 text-center text-gray-500 text-sm">
      <p className="mb-3">{label}: No image generated yet.</p>
      {onGenerate && (
        <button
          onClick={onGenerate}
          disabled={generating}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Generate {label}
        </button>
      )}
    </div>
  )
);

const LogoImageWidget = ({ src, label, onGenerate, generating }) => (
  src ? (
    <div
      className="bg-white border border-gray-300 p-2 relative group"
      style={{ overflow: 'auto', maxHeight: '80vh', marginLeft: '-1rem', marginRight: '-1rem' }}
    >
      <img src={src} alt={label} style={{ display: 'block', maxWidth: 'none', height: 'auto' }} />
      {onGenerate && (
        <button
          onClick={onGenerate}
          disabled={generating}
          className="sticky top-2 right-2 float-right opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-xs text-white z-10"
          title={`Regenerate ${label}`}
        >
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      )}
    </div>
  ) : (
    <div className="bg-gray-900 border border-gray-700  py-10 text-center text-gray-500 text-sm">
      <p className="mb-3">{label}: No image generated yet.</p>
      {onGenerate && (
        <button
          onClick={onGenerate}
          disabled={generating}
          className="inline-flex items-center gap-2 px-4 py-2  bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Generate {label}
        </button>
      )}
    </div>
  )
);

/* ──────────────────────── Widget registry ──────────────────────── */
// widgetKey -> (ctx) => JSX. Single source for all report views.
export const WIDGET_RENDERERS = {
  // Shared issue widgets — product-aware appearance.
  highlight_issues: (ctx) => (ctx.product === 'Software' ? <SwHighlight data={ctx.data} /> : <HwHighlight data={ctx.data} />),
  state_summary: (ctx) => <StateSummary data={ctx.data} stateCell={ctx.product === 'Software' ? SW_STATE_CELL : HW_STATE_CELL} />,
  severity_pie_chart: (ctx) => <SeverityPie slices={ctx.data?.severityOverview?.slices || []} light />,

  // Hardware-only.
  issue_domain_chart: (ctx) => <DomainBars bars={ctx.data?.issueDomain || []} light />,

  // Software-only.
  function_domain_chart: (ctx) => <DomainBars bars={ctx.data?.functionDomain || []} light />,
  stability_table: (ctx) => <SwStability data={ctx.data} />,

  // Certification.
  cert_highlight: (ctx) => <CertHighlight data={ctx.data} />,
  cert_weekly_highlight: (ctx) => <CertWeeklyHighlight data={ctx.data} />,
  cert_state_table: (ctx) => <CertStateTable data={ctx.data} />,
  cert_state_severity: (ctx) => <CertStateSeverity data={ctx.data} />,
  cert_owner_matrix: (ctx) => <CertOwnerMatrix data={ctx.data} />,
  cert_trend_chart: (ctx) => <ImageWidget src={ctx.images?.trend} label="Trend Chart" onGenerate={ctx.onGenerateCharts} generating={ctx.imgBusy?.charts} />,
  cert_throughput_chart: (ctx) => <ImageWidget src={ctx.images?.throughput} label="Throughput Chart" onGenerate={ctx.onGenerateCharts} generating={ctx.imgBusy?.charts} />,
  cert_logo_overview: (ctx) => <LogoImageWidget src={ctx.images?.logo} label="Logo Status Overview" onGenerate={ctx.onGenerateLogo} generating={ctx.imgBusy?.logo} />,
  cs_cg_cg_delay: (ctx) => <Table columns={['Certification', 'Status', 'Target CS', 'Actual CS', 'Target CG', 'Actual CG', 'Remark']} keys={['certification', 'status', 'targetCs', 'actualCs', 'targetCg', 'actualCg', 'remark']} rows={ctx.data?.cgDelay} empty="No delay items this week" />,
  cs_cg_cs_delay: (ctx) => <Table columns={['Certification', 'Scope', 'Status', 'Target CS', 'Target CG', 'Coverage', 'Remark']} keys={['certification', 'scope', 'status', 'targetCs', 'targetCg', 'coverage', 'remark']} rows={ctx.data?.csDelay} empty="No delay items this week" />,
  cs_cg_cs_this_week: (ctx) => <Table columns={['Certification', 'Scope', 'Status', 'Coverage', 'Target CS', 'Target CG', 'Remark']} keys={['certification', 'scope', 'status', 'coverage', 'targetCs', 'targetCg', 'remark']} rows={ctx.data?.csThisWeek} empty="No CS items this week" />,
  cs_cg_summary: (ctx) => <Table columns={['Category', 'Total', 'Certified', 'Pretest', 'LAB']} keys={['category', 'total', 'certified', 'pretest', 'lab']} rows={ctx.data?.summary} empty="No data" />,
};

// Render a widget by key. Returns a graceful fallback if no renderer exists.
export function renderWidget(widgetKey, ctx = {}) {
  const fn = WIDGET_RENDERERS[widgetKey];
  if (!fn) {
    return <div className="text-gray-500 text-xs sm:text-sm p-3 sm:p-4">{ctx.widgetName || widgetKey} (no renderer)</div>;
  }
  return fn(ctx);
}
